import React, { useMemo } from 'react'
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Avatar,
    Chip,
    Stack,
    CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useGetProjectTrackerProjectById } from 'services/api/task-management/trackerHooks'
import { getProjectColorTokens } from './projectCalendarColors'

const formatMonthDateYear = (iso) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'

    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

const diffDaysInclusive = (startIso, endIso) => {
    if (!startIso || !endIso) return null
    const s = new Date(startIso)
    const e = new Date(endIso)
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null
    const ms = e.getTime() - s.getTime()
    if (ms < 0) return null
    return Math.ceil(ms / (1000 * 60 * 60 * 24)) + 1
}

const statusLabel = (s) => {
    if (!s) return '—'
    return String(s)
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
}

const getAvatarUrl = (staff) =>
    staff?.profile_image_url || staff?.staff_profile_image?.profile_image_url || ''

const initials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

export default function TrackerCalendarDrawer({ open, onClose, projectId }) {
    const { data, isLoading, isFetching, isError, error } = useGetProjectTrackerProjectById(
        projectId,
        {
            enabled: open && Boolean(projectId),
        }
    )

    const project = data?.data?.project
    const colorTokens = getProjectColorTokens(project)
    const title = project?.name || 'Untitled Project'
    const start = formatMonthDateYear(project?.start_date)
    const end = formatMonthDateYear(project?.deadline)

    const durationDays = useMemo(
        () => diffDaysInclusive(project?.start_date, project?.deadline),
        [project?.start_date, project?.deadline]
    )

    const tasks = project?.tasks ?? []

    const uniqueMembers = useMemo(() => {
        const list = project?.members ?? []
        const map = new Map()

        list.forEach((m) => {
            const staff = m?.staff
            const staffId = staff?.id ?? m?.staff_id
            if (!staffId) return

            if (!map.has(staffId)) {
                map.set(staffId, { staff, roles: new Set() })
            }

            map.get(staffId).staff = staff
            if (m?.role) map.get(staffId).roles.add(m.role)
        })

        return Array.from(map.values()).map((v) => ({
            staff: v.staff,
            roles: Array.from(v.roles),
        }))
    }, [project?.members])

    const memberByStaffId = useMemo(() => {
        const map = new Map()
        const list = project?.members ?? []
        list.forEach((m) => {
            const staff = m?.staff
            if (staff?.id) map.set(staff.id, staff)
        })
        return map
    }, [project?.members])

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                sx={{
                    width: { xs: '100%', sm: 400, md: 450, lg: 500, xl: 800 },
                    height: '100%',
                    bgcolor: '#fff',
                }}
            >
                <Box
                    sx={{
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        background: colorTokens.accentSurface,
                        borderBottom: `1px solid ${colorTokens.accentBorder}`,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.25,
                                py: 0.5,
                                mb: 1,
                                borderRadius: 999,
                                bgcolor: colorTokens.accent,
                                color: colorTokens.accentStrongText,
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Project
                        </Box>

                        <Typography
                            variant="h3"
                            fontWeight={600}
                            sx={{ color: colorTokens.accentText, minWidth: 0, wordBreak: 'break-word' }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {isLoading ? (
                    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography color="text.secondary">Loading project…</Typography>
                    </Box>
                ) : isError ? (
                    <Box sx={{ p: 3 }}>
                        <Typography color="error">
                            Failed to load project. {error?.message ? `(${error.message})` : ''}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Timeline */}
                        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: colorTokens.accentSoft,
                                    mt: 0.5,
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M19 19H5V8H19M16 1V3H8V1H6V3H5C3.89 3 3 3.89 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3H18V1M17 12H12V17H17V12Z"
                                        fill={colorTokens.accent}
                                    />
                                </svg>
                            </Box>

                            <Box>
                                <Typography variant="body1" fontWeight={400} color="text.secondary" mt={0.5}>
                                    Project Timeline:
                                </Typography>

                                <Typography variant="h4" fontWeight={500} mb={0.5} sx={{ color: '#3A3A3A' }}>
                                    {start} {end && end !== '-' ? `– ${end}` : ''}
                                </Typography>

                                {durationDays ? (
                                    <Typography variant="body1" fontWeight={400} color="text.secondary">
                                        {durationDays} days
                                    </Typography>
                                ) : null}

                                {isFetching && !isLoading ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Updating…
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>

                        <Divider />

                        {/* Members */}
                        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: colorTokens.accentSoft,
                                    mt: 0.5,
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M18.8439 7.875C18.7065 9.78141 17.2923 11.25 15.7501 11.25C14.2079 11.25 12.7914 9.78188 12.6564 7.875C12.5157 5.89172 13.8925 4.5 15.7501 4.5C17.6078 4.5 18.9845 5.92781 18.8439 7.875Z"
                                        stroke={colorTokens.accent}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M15.7498 14.25C12.695 14.25 9.75731 15.7673 9.02138 18.7223C8.92388 19.1133 9.16903 19.5 9.57075 19.5H21.9293C22.3311 19.5 22.5748 19.1133 22.4787 18.7223C21.7428 15.72 18.8051 14.25 15.7498 14.25Z"
                                        stroke={colorTokens.accent}
                                        strokeWidth="1.5"
                                        strokeMiterlimit="10"
                                    />
                                    <path
                                        d="M9.37496 8.71594C9.26527 10.2384 8.12246 11.4375 6.89058 11.4375C5.65871 11.4375 4.51402 10.2389 4.40621 8.71594C4.29418 7.13203 5.40652 6 6.89058 6C8.37465 6 9.48699 7.16109 9.37496 8.71594Z"
                                        stroke={colorTokens.accent}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M9.65671 14.3434C8.81061 13.9557 7.87874 13.8066 6.89108 13.8066C4.45358 13.8066 2.10515 15.0184 1.51686 17.3785C1.43952 17.6907 1.63546 17.9996 1.95608 17.9996H7.21921"
                                        stroke={colorTokens.accent}
                                        strokeWidth="1.5"
                                        strokeMiterlimit="10"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight={400} color="text.secondary" mt={0.5} mb={1}>
                                    Team Members ({uniqueMembers.length})
                                </Typography>

                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {uniqueMembers.map(({ staff, roles }) => {
                                        const name = staff?.name || 'Unknown'
                                        const img = getAvatarUrl(staff)

                                        return (
                                            <Chip
                                                key={staff?.id || name}
                                                avatar={<Avatar src={img}>{initials(name)}</Avatar>}
                                                label={name}
                                                title={roles?.length ? roles.join(', ') : ''}
                                                variant="outlined"
                                            />
                                        )
                                    })}
                                </Stack>
                            </Box>
                        </Box>

                        <Divider />

                        <Box sx={{ px: 3, pb: 3 }}>
                            <Typography variant="h4" fontWeight={500} sx={{ mb: 1.5, mt: 2 }}>
                                Tasks ({tasks.length})
                            </Typography>

                            <Box sx={{ border: '1px solid #EEE', borderRadius: 2, overflow: 'hidden' }}>
                                {tasks.length === 0 ? (
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No tasks.
                                        </Typography>
                                    </Box>
                                ) : (
                                    tasks.map((t, idx) => {
                                        const staff = memberByStaffId.get(t?.assigned_to)
                                        const staffName = staff?.name || 'Unassigned'
                                        const avatarUrl = getAvatarUrl(staff)

                                        return (
                                            <Box
                                                key={t?.id ?? idx}
                                                sx={{
                                                    p: 2,
                                                    borderBottom: idx === tasks.length - 1 ? 'none' : '1px solid #EEE',
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <Avatar
                                                    src={avatarUrl}
                                                    sx={{ width: 36, height: 36, mt: 0.2 }}
                                                    imgProps={{ referrerPolicy: 'no-referrer' }}
                                                >
                                                    {initials(staffName)}
                                                </Avatar>

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body1" fontWeight={400} mb={0.5}>
                                                        {t?.name || t?.title || 'Untitled Task'}
                                                    </Typography>

                                                    <Typography variant="body2" fontWeight={400} color="text.secondary">
                                                        {statusLabel(t?.status)}
                                                        {t?.due_date ? ` • Due: ${formatMonthDateYear(t.due_date)}` : ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )
                                    })
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Drawer>
    )
}
